import { Navigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Flex,
  List,
  Row,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import { ClearOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { PipelineDebugHeader } from '~/app/components/pipeline-debug/PipelineDebug/components/PipelineDebugHeader/PipelineDebugHeader';
import { PipelineFlowCard } from '~/app/components/pipeline-debug/PipelineDebug/components/PipelineFlowCard/PipelineFlowCard';
import { usePipelineUploadColumns } from '~/app/components/pipeline-debug/PipelineDebug/hooks/usePipelineUploadColumns';
import { PIPELINE_STEP_EXAMPLES } from '~/app/components/pipeline-debug/PipelineDebug/pipeline-examples';
import {
  copyJsonToClipboard,
  downloadJsonFile,
  parseJsonFileContent,
} from '~/app/components/pipeline-debug/PipelineDebug/utils';
import {
  completedStepCount,
  totalUploadedRows,
} from '~/app/components/pipeline-debug/PipelineDebug/utils/step-display';
import { useAuth } from '~/app/auth/context/AuthContext';
import { PipelinePassChecklist } from './components/PipelinePassChecklist/PipelinePassChecklist';
import { useSupervisorManualUploadPipeline } from './hooks/useSupervisorManualUploadPipeline';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

const { Text } = Typography;
const { Dragger } = Upload;

function toDisplayNamePillLabel(
  displayName: string,
  tenantAssetId: string,
): string {
  const trimmedDisplayName = displayName.trim();
  const sandboxPrefix = /^sandbox\s+asset\s+/i;
  const cleaned = trimmedDisplayName.replace(sandboxPrefix, '').trim();

  if (!cleaned) {
    return tenantAssetId;
  }

  return cleaned;
}

export function ManualUploadScreen() {
  const { t } = useTranslation(['manualUpload', 'pipelineDebug']);
  const { user } = useAuth();
  const { columns } = usePipelineUploadColumns();
  const {
    runMode,
    activeStepIndex,
    setActiveStepIndex,
    activeStepId,
    activeStep,
    uploads,
    steps,
    result,
    canRun,
    isBusy,
    isResetting,
    ingestLinFile,
    runFromActiveStep,
    continuePipeline,
    resetSandbox,
    setStepInput,
    setStepOutput,
  } = useSupervisorManualUploadPipeline();

  if (!user || user.roleName !== SystemRoleName.SUPERVISOR) {
    return <Navigate to="/home" replace />;
  }

  const deducedAssets = result?.deducedAssets ?? [];
  const activeExamples = PIPELINE_STEP_EXAMPLES[activeStepId];

  return (
    <div className="app-page pipeline-debug-page">
      <PipelineDebugHeader
        uploads={uploads}
        completedSteps={completedStepCount(steps)}
      />

      <Row gutter={[16, 16]} align="top">
        <Col xs={24} lg={8}>
          <Flex vertical gap={16}>
            <PipelinePassChecklist steps={steps} activeStepId={activeStepId} />

            <Card
              title={t('pipelineDebug:setup.title')}
              className="pipeline-debug-card"
              extra={
                <Button
                  icon={<ClearOutlined />}
                  loading={isResetting}
                  disabled={isBusy}
                  onClick={() => {
                    void resetSandbox();
                  }}
                >
                  {t('supervisor.resetSandbox')}
                </Button>
              }
              styles={{ body: { padding: 16 } }}
            >
              <Flex vertical gap={14}>
                <div>
                  <Text strong>{t('supervisor.deducedAssets')}</Text>
                  <div style={{ marginTop: 8 }}>
                    {deducedAssets.length > 0 ? (
                      <List
                        size="small"
                        dataSource={deducedAssets}
                        renderItem={(asset) => {
                          const displayNamePill = toDisplayNamePillLabel(
                            asset.displayName,
                            asset.tenantAssetId,
                          );

                          return (
                            <List.Item>
                              <Flex gap={8} wrap style={{ width: '100%' }}>
                                <Tag color="blue">{displayNamePill}</Tag>
                                {asset.model ? (
                                  <Tag color="geekblue">{asset.model}</Tag>
                                ) : null}
                              </Flex>
                            </List.Item>
                          );
                        }}
                      />
                    ) : (
                      <Text type="secondary">
                        {t('supervisor.assetCount', { count: 0 })}
                      </Text>
                    )}
                  </div>
                </div>

                <Dragger
                  multiple={false}
                  accept=".json,.txt"
                  className="pipeline-debug-upload"
                  beforeUpload={(file) => {
                    void ingestLinFile(file).catch((parseError: unknown) => {
                      const parseErrorMessage =
                        parseError instanceof Error
                          ? parseError.message
                          : t('supervisor.uploadFailed');
                      message.error(parseErrorMessage);
                    });
                    return false;
                  }}
                  showUploadList={false}
                  disabled={isBusy}
                >
                  <Flex
                    vertical
                    align="center"
                    gap={6}
                    style={{ padding: '4px 0' }}
                  >
                    <InboxOutlined style={{ fontSize: 32, color: '#1E6DF6' }} />
                    <Text strong style={{ fontSize: 14 }}>
                      {t('supervisor.dropzoneTitle')}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {t('supervisor.dropzoneHint')}
                    </Text>
                  </Flex>
                </Dragger>

                <div className="pipeline-debug-file-table">
                  <EvimoTable
                    className="app-table"
                    columns={columns}
                    dataSource={uploads}
                    rowKey="id"
                    pagination={false}
                    locale={{ emptyText: t('pipelineDebug:upload.empty') }}
                    size="small"
                  />
                </div>

                <Text type="secondary">
                  {t('pipelineDebug:kpis.logRows')}:{' '}
                  {totalUploadedRows(uploads)}
                </Text>
              </Flex>
            </Card>
          </Flex>
        </Col>

        <Col xs={24} lg={16}>
          <PipelineFlowCard
            runMode={runMode}
            activeStepIndex={activeStepIndex}
            activeStepId={activeStepId}
            activeStep={activeStep}
            steps={steps}
            canRun={canRun}
            exampleInput={activeExamples.input}
            exampleOutput={activeExamples.output}
            onStepSelect={setActiveStepIndex}
            onRun={() => {
              void runFromActiveStep();
            }}
            onContinue={() => {
              void continuePipeline();
            }}
            onUpdateInput={(value) => {
              setStepInput(activeStepId, value);
            }}
            onUpdateOutput={(value) => {
              setStepOutput(activeStepId, value);
            }}
            onCopyInput={() => {
              void copyJsonToClipboard(activeStep.input).then((copied) => {
                if (copied) {
                  message.success(t('pipelineDebug:messages.copied'));
                }
              });
            }}
            onCopyOutput={() => {
              void copyJsonToClipboard(activeStep.output).then((copied) => {
                if (copied) {
                  message.success(t('pipelineDebug:messages.copied'));
                }
              });
            }}
            onDownloadInput={() => {
              downloadJsonFile(`${activeStepId}-input.json`, activeStep.input);
            }}
            onDownloadOutput={() => {
              downloadJsonFile(
                `${activeStepId}-output.json`,
                activeStep.output,
              );
            }}
            onCopyExampleInput={() => {
              void copyJsonToClipboard(activeExamples.input).then((copied) => {
                if (copied) {
                  message.success(t('pipelineDebug:messages.copied'));
                }
              });
            }}
            onCopyExampleOutput={() => {
              void copyJsonToClipboard(activeExamples.output).then((copied) => {
                if (copied) {
                  message.success(t('pipelineDebug:messages.copied'));
                }
              });
            }}
            onDownloadExampleInput={() => {
              downloadJsonFile(
                `${activeStepId}-example-input.json`,
                activeExamples.input,
              );
            }}
            onDownloadExampleOutput={() => {
              downloadJsonFile(
                `${activeStepId}-example-output.json`,
                activeExamples.output,
              );
            }}
            onStepInputFilesSelected={(files) => {
              const [firstFile] = files;
              if (!firstFile) {
                return;
              }

              void firstFile.text().then((content) => {
                const parsed = parseJsonFileContent(firstFile.name, content);
                if ('error' in parsed) {
                  message.error(parsed.error);
                  return;
                }

                const parsedRecords = parsed.records;
                const input =
                  activeStepId === 'lin'
                    ? parsedRecords
                    : parsedRecords.length === 1
                      ? parsedRecords[0]
                      : parsedRecords;

                setStepInput(activeStepId, input, false);
                message.success(
                  t('pipelineDebug:messages.stepInputUploaded', {
                    count: files.length,
                  }),
                );
              });
            }}
            isUploadDisabled={isBusy}
          />
        </Col>
      </Row>
    </div>
  );
}
